import React from 'react';

import { Avatar } from '@edx/components/avatar';
import { Link } from '@edx/components/link';
import { TeacherEndorsement } from '@edx/graphql/generated';
import IconFacebookOutline from '@edx/components/icons/icon-facebook-outline';
import IconYoutubeOutline from '@edx/components/icons/icon-youtube-outline';
import IconTwitterOutline from '@edx/components/icons/icon-twitter-outline';
import IconInstagramOutline from '@edx/components/icons/icon-instagram-outline';
import { Body, Caption, Title } from '@edx/components/typography';

export type Props = {
  name: string;
  tagline: string;
  avatarUrl?: string | null;
  location?: string | null;
  yearBorn?: number | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  twitterUrl?: string | null;
  youtubeUrl?: string | null;
  endorsements?: TeacherEndorsement[];
};

export const TeacherProfile: React.FC<Props> = ({
  name,
  tagline,
  avatarUrl,
  location,
  yearBorn,
  facebookUrl,
  instagramUrl,
  twitterUrl,
  youtubeUrl,
  endorsements,
}) => (
  <div className="sticky top-8 text-gray-500 bg-gray-800 bg-opacity-50">
    <div className="flex items-center bg-gray-800 p-4 space-x-3">
      <Avatar name={name} src={avatarUrl} size={64} />

      <div>
        <Title tag="h4" size="small" className="text-gray-400">
          {name}
        </Title>
        {location && yearBorn ? `${location}, ${yearBorn}` : location || yearBorn}
      </div>
    </div>

    <div className="p-6 space-y-4">
      <div className="flex flex-wrap space-x-4">
        {youtubeUrl && (
          <Link href={youtubeUrl} className="w-10 h-10 p-2 rounded-full border-2 border-gray-600 text-gray-300">
            <IconYoutubeOutline className="w-5 h-5" />
          </Link>
        )}
        {facebookUrl && (
          <Link href={facebookUrl} className="w-10 h-10 p-2 rounded-full border-2 border-gray-600 text-gray-300">
            <IconFacebookOutline className="w-5 h-5" />
          </Link>
        )}
        {twitterUrl && (
          <Link href={twitterUrl} className="w-10 h-10 p-2 rounded-full border-2 border-gray-600 text-gray-300">
            <IconTwitterOutline className="w-5 h-5" />
          </Link>
        )}
        {instagramUrl && (
          <Link href={instagramUrl} className="w-10 h-10 p-2 rounded-full border-2 border-gray-600 text-gray-300">
            <IconInstagramOutline className="w-5 h-5" />
          </Link>
        )}
      </div>

      <Body className="leading-snug">{tagline}</Body>
    </div>

    {Array.isArray(endorsements) && endorsements.length > 0 && (
      <div className="border-t border-gray-700 p-6 space-y-6">
        <Caption variant="semibold" className="tracking-widest">
          Patrocinado por
        </Caption>

        <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-3 gap-6">
          {endorsements.map(({ name, imageUrl, websiteUrl }) => (
            <li key={name} className="relative flex justify-center items-center">
              {websiteUrl ? (
                <Link href={websiteUrl}>
                  <img
                    src={imageUrl as string}
                    alt={name}
                    className="opacity-60 hover:opacity-100 transition-all duration-100 ease"
                  />
                </Link>
              ) : (
                <img
                  src={imageUrl as string}
                  alt={name}
                  className="opacity-60 hover:opacity-100 transition-all duration-100 ease"
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);
